/**
 * Grupos prontos: a receita (`SnippetSpec`) vira passos e ligações.
 *
 * Envio com reenvio = Envio → Acompanhar entrega → (Falha) → Envio → …
 * Inatividade        = Acompanhar entrega → (Parado) → Cancelar entrega →
 *                      (Cancelado) → Envio → Acompanhar entrega → …
 * Envio com reenvio e inatividade = o Envio com reenvio com as duas regras:
 *                      pela Falha envia de novo direto; pelo Parado cancela
 *                      antes. As tentativas são as mesmas para as duas saídas.
 *                      É gravado como `kind: 'envio'` (o back só conhece os
 *                      tipos de grupo de sempre); o que muda é a receita.
 *
 * Cada tentativa é um Envio seguido de um Acompanhar entrega. Com regra de
 * Parado, um Cancelar entrega vem antes do Envio: a entrega parada ainda
 * está viva (a validação do back exige). Pela Falha, o Envio vem direto.
 *
 * O grupo é remontado sempre que a receita muda (`rebuildSnippet`): passo
 * que continua no mesmo papel mantém id, posição e config.
 *
 * `expandReenvio` monta o grupo antigo (Aguardar evento + Envio), que só
 * existe em grafos salvos antes do Acompanhar entrega.
 */

import { nanoid } from 'nanoid';
import type { WorkflowCatalog } from '../api/types';
import type {
  NodeKind,
  SnippetSpec,
  TrackDeliveryConfig,
  TrackRule,
  WorkflowEdge,
  WorkflowGraph,
  WorkflowNode,
  WorkflowSnippet,
} from '../types';
import { NODE_HEIGHT, NODE_SEP, NODE_WIDTH, RANK_SEP } from '../canvas/layout';
import {
  dispatchKindOf,
  edgeStyleForPort,
  eventStatusCodes,
  outputPortIds,
  primaryOutputPortId,
  trackStatusCodes,
} from './catalog';
import { makeNode, newId } from './graph-ops';
import { nodesRoomLeft } from './graph-limits';
import { TRACK_MAX_MINUTES } from './track-rules';

/**
 * Passo horizontal entre os nós recém-criados: o canvas é LR (esquerda →
 * direita), então a chain nasce já na mesma coluna/linha que o Organizar
 * automaticamente produziria.
 */
const STEP_X = NODE_WIDTH + RANK_SEP;
const STEP_Y = NODE_HEIGHT + NODE_SEP;

export const SNIPPET_MAX_ATTEMPTS = 5;

/** As falhas que acionavam a regra de reenvio antiga. */
const DEFAULT_FAIL_STATUSES = [
  'ORDER_FAILED:CARRIER_ERROR',
  'ORDER_FAILED:CARRIER_TIMEOUT',
  'ORDER_FAILED:CARRIER_CANCELED',
  'ORDER_FAILED:OUT_OF_COVERAGE',
];
const DEFAULT_STUCK_STATUS = 'PENDING:WAITING_FOR_CARRIER';
const DEFAULT_STUCK_MINUTES = 10;
const CANCEL_REASON = 'Inatividade: pedido parado na transportadora';

/**
 * Receita de grupo novo que a paleta oferece. Não é o `kind` gravado: o
 * preset `envio-inatividade` sai como `kind: 'envio'` com as duas regras.
 */
export type SpecSnippetKind = 'envio' | 'envio-inatividade' | 'inatividade';

/** `kind` gravado no grafo para cada receita. */
export const SNIPPET_PRESET_KIND: Record<
  SpecSnippetKind,
  'envio' | 'inatividade'
> = {
  envio: 'envio',
  'envio-inatividade': 'envio',
  inatividade: 'inatividade',
};

/** Nome do grupo novo de cada receita. */
export const SNIPPET_PRESET_LABEL: Record<SpecSnippetKind, string> = {
  envio: 'Envio com reenvio',
  'envio-inatividade': 'Envio com reenvio e inatividade',
  inatividade: 'Inatividade',
};

export const SNIPPET_LABEL: Record<WorkflowSnippet['kind'], string> = {
  envio: 'Envio com reenvio',
  inatividade: 'Inatividade',
  reenvio: 'Reenvio',
};

export interface SnippetExpansion {
  snippet: WorkflowSnippet;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  /** Primeiro nó da chain — pra conectar com o predecessor */
  entryNodeId: string;
}

/* ----- Receita ----- */

const ruleId = () => `r_${nanoid(6)}`;

export const specHasRule = (spec: SnippetSpec, output: TrackRule['output']) =>
  spec.track.rules.some((r) => r.output === output);

/** Regra de Falha padrão: as falhas da regra de reenvio antiga, no escopo. */
function failedRule(catalog: WorkflowCatalog): TrackRule {
  const scope = trackStatusCodes(catalog, 'failed');
  return {
    id: ruleId(),
    output: 'failed',
    statuses: DEFAULT_FAIL_STATUSES.filter((c) => scope.includes(c)),
  };
}

/** Regra de Parado padrão: "Aguardando transportadora" por 10 minutos. */
function stuckRule(catalog: WorkflowCatalog): TrackRule {
  const scope = trackStatusCodes(catalog, 'stuck');
  const status = scope.includes(DEFAULT_STUCK_STATUS)
    ? DEFAULT_STUCK_STATUS
    : scope[0];
  return {
    id: ruleId(),
    output: 'stuck',
    statuses: status ? [status] : [],
    minutes: DEFAULT_STUCK_MINUTES,
  };
}

/**
 * Receita de um grupo novo. Envio com reenvio: 2 reenvios pela Falha (as
 * falhas da regra de reenvio antiga). Envio com reenvio e inatividade: as
 * mesmas 2 tentativas, pela Falha ou pelo Parado. Inatividade: 1 novo envio
 * pelo Parado em "Aguardando transportadora" por 10 minutos. Status fora do
 * escopo do catálogo ficam de fora.
 */
export function defaultSnippetSpec(
  catalog: WorkflowCatalog,
  kind: SpecSnippetKind,
): SnippetSpec {
  if (kind === 'envio' || kind === 'envio-inatividade') {
    return {
      startsWithDispatch: true,
      attempts: 2,
      onExhausted: 'end',
      track: {
        maxMinutes: TRACK_MAX_MINUTES,
        rules:
          kind === 'envio'
            ? [failedRule(catalog)]
            : [failedRule(catalog), stuckRule(catalog)],
      },
    };
  }
  return {
    startsWithDispatch: false,
    attempts: 1,
    onExhausted: 'end',
    track: {
      maxMinutes: TRACK_MAX_MINUTES,
      rules: [stuckRule(catalog)],
    },
  };
}

/**
 * Tentativas que a receita monta de fato. Sem regra de Falha nem de
 * Parado, nada leva à próxima tentativa: o grupo fica só no primeiro
 * acompanhamento.
 */
export function effectiveAttempts(spec: SnippetSpec): number {
  if (!specHasRule(spec, 'failed') && !specHasRule(spec, 'stuck')) return 0;
  return Math.min(
    SNIPPET_MAX_ATTEMPTS,
    Math.max(1, Math.round(spec.attempts)),
  );
}

/** Passos que cada tentativa acrescenta (Envio, Acompanhar e, com Parado, Cancelar). */
export const stepsPerAttempt = (spec: SnippetSpec): number =>
  specHasRule(spec, 'stuck') ? 3 : 2;

/* ----- Plano: papéis e ligações ----- */

type Role = string;
type PlannedPort = 'primary' | 'stuck' | 'failed';

interface Plan {
  roles: Role[];
  links: Array<{ from: Role; port: PlannedPort; to: Role }>;
  entry: Role;
}

const attemptOf = (role: Role) => Number(role.split(':')[1] ?? 0);
const roleName = (role: Role) => role.split(':')[0];

function planSnippet(spec: SnippetSpec): Plan {
  const hasFail = specHasRule(spec, 'failed');
  const hasStuck = specHasRule(spec, 'stuck');
  const attempts = effectiveAttempts(spec);
  const roles: Role[] = [];
  const links: Plan['links'] = [];

  if (spec.startsWithDispatch) {
    roles.push('disp:0', 'track:0');
    links.push({ from: 'disp:0', port: 'primary', to: 'track:0' });
  } else {
    roles.push('track:0');
  }

  for (let i = 1; i <= attempts; i++) {
    const prevTrack = `track:${i - 1}`;
    const disp = `disp:${i}`;
    if (hasStuck) {
      const cancel = `cancel:${i - 1}`;
      roles.push(cancel);
      links.push({ from: prevTrack, port: 'stuck', to: cancel });
      links.push({ from: cancel, port: 'primary', to: disp });
    }
    roles.push(disp);
    if (hasFail) links.push({ from: prevTrack, port: 'failed', to: disp });
    roles.push(`track:${i}`);
    links.push({ from: disp, port: 'primary', to: `track:${i}` });
  }

  if (spec.onExhausted === 'tag' && attempts > 0) {
    const lastTrack = `track:${attempts}`;
    roles.push('tag');
    if (hasFail) links.push({ from: lastTrack, port: 'failed', to: 'tag' });
    if (hasStuck) links.push({ from: lastTrack, port: 'stuck', to: 'tag' });
  }

  return { roles, links, entry: spec.startsWithDispatch ? 'disp:0' : 'track:0' };
}

/* ----- Passos ----- */

function kindOfRole(catalog: WorkflowCatalog, role: Role): NodeKind {
  switch (roleName(role)) {
    case 'disp':
      return dispatchKindOf(catalog);
    case 'track':
      return 'track';
    case 'cancel':
      return 'cancel';
    default:
      return 'apply-tag';
  }
}

const trackRulesOf = (spec: SnippetSpec): TrackRule[] =>
  spec.track.rules.map((r) => ({ ...r, statuses: [...r.statuses] }));

/** Config de um passo novo do grupo; `undefined` = o padrão do catálogo. */
function freshConfig(
  catalog: WorkflowCatalog,
  role: Role,
  spec: SnippetSpec,
): Record<string, unknown> | undefined {
  const i = attemptOf(role);
  switch (roleName(role)) {
    case 'disp':
      if (dispatchKindOf(catalog) === 'offer') return undefined;
      return i === 0
        ? { title: '', action: 'CHEAPEST_METHOD' }
        : {
            title: '',
            // 2º reenvio vai pelo mais rápido; os outros, pelo mais barato.
            action: i === 2 ? 'FASTEST_METHOD' : 'CHEAPEST_METHOD',
            excludeTried: true,
          };
    case 'track':
      return {
        rules: trackRulesOf(spec),
        maxMinutes: spec.track.maxMinutes,
        carrier: null,
      };
    case 'cancel':
      return { reason: CANCEL_REASON, notifyCarrier: true };
    default:
      return undefined;
  }
}

/**
 * Posição de cada papel: o que já existe fica onde está; o novo vem ao lado
 * do passo anterior da chain. Cancelar sobe uma linha quando a Falha também
 * liga o mesmo Acompanhar ao próximo Envio.
 */
function layoutRoles(
  plan: Plan,
  spec: SnippetSpec,
  anchor: { x: number; y: number },
  existing: Map<Role, WorkflowNode>,
): Record<Role, { x: number; y: number }> {
  const hasStuck = specHasRule(spec, 'stuck');
  const both = hasStuck && specHasRule(spec, 'failed');
  const last = effectiveAttempts(spec);
  const pos: Record<Role, { x: number; y: number }> = {};
  const at = (role: Role) => pos[role] ?? anchor;
  for (const role of plan.roles) {
    const prev = existing.get(role);
    if (prev) {
      pos[role] = prev.position;
      continue;
    }
    const i = attemptOf(role);
    if (role === plan.entry) {
      pos[role] = anchor;
    } else if (roleName(role) === 'track') {
      const d = at(`disp:${i}`);
      pos[role] = { x: d.x + STEP_X, y: d.y };
    } else if (roleName(role) === 'cancel') {
      const t = at(`track:${i}`);
      pos[role] = { x: t.x + STEP_X, y: both ? t.y - STEP_Y : t.y };
    } else if (roleName(role) === 'disp') {
      const t = at(`track:${i - 1}`);
      pos[role] = { x: t.x + STEP_X * (hasStuck ? 2 : 1), y: t.y };
    } else {
      const t = at(`track:${last}`);
      pos[role] = { x: t.x + STEP_X, y: t.y };
    }
  }
  return pos;
}

export interface SnippetBuild {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  /** Papel de cada passo, por id. */
  roles: Record<string, Role>;
  entryNodeId: string;
}

/**
 * Passos e ligações da receita. `existing` (por papel) reaproveita os
 * passos de uma montagem anterior: mesmo id, posição e config; o
 * Acompanhar entrega recebe as regras e o prazo da receita.
 */
export function buildSnippet(
  catalog: WorkflowCatalog,
  spec: SnippetSpec,
  snippetId: string,
  anchor: { x: number; y: number },
  existing: Map<Role, WorkflowNode> = new Map(),
): SnippetBuild {
  const plan = planSnippet(spec);
  const pos = layoutRoles(plan, spec, anchor, existing);
  const byRole = new Map<Role, WorkflowNode>();

  for (const role of plan.roles) {
    const kind = kindOfRole(catalog, role);
    const prev = existing.get(role);
    let node: WorkflowNode;
    if (prev && prev.kind === kind) {
      node =
        kind === 'track'
          ? ({
              ...prev,
              config: {
                ...(prev.config as TrackDeliveryConfig),
                rules: trackRulesOf(spec),
                maxMinutes: spec.track.maxMinutes,
              },
            } as WorkflowNode)
          : prev;
    } else {
      node = makeNode(
        catalog,
        kind,
        pos[role],
        freshConfig(catalog, role, spec) as never,
      );
    }
    byRole.set(role, { ...node, snippetId });
  }

  const edges: WorkflowEdge[] = plan.links.map((l) => {
    const from = byRole.get(l.from) as WorkflowNode;
    const to = byRole.get(l.to) as WorkflowNode;
    const portId =
      l.port === 'primary' ? primaryOutputPortId(catalog, from) : l.port;
    return {
      id: newId('edge'),
      fromNodeId: from.id,
      fromPortId: portId,
      toNodeId: to.id,
      style: edgeStyleForPort(catalog, from, portId),
    };
  });

  const roles: Record<string, Role> = {};
  byRole.forEach((node, role) => {
    roles[node.id] = role;
  });

  return {
    nodes: [...byRole.values()],
    edges,
    roles,
    entryNodeId: (byRole.get(plan.entry) as WorkflowNode).id,
  };
}

/** Grupo novo (uma das receitas da paleta) com a receita padrão. */
export function expandSpecSnippet(
  catalog: WorkflowCatalog,
  preset: SpecSnippetKind,
  originPosition = { x: 0, y: 0 },
): SnippetExpansion {
  const spec = defaultSnippetSpec(catalog, preset);
  const snippetId = `snippet_${nanoid(6)}`;
  const built = buildSnippet(catalog, spec, snippetId, originPosition);
  return {
    snippet: {
      id: snippetId,
      kind: SNIPPET_PRESET_KIND[preset],
      nodeIds: built.nodes.map((n) => n.id),
      label: SNIPPET_PRESET_LABEL[preset],
      spec,
      roles: built.roles,
    },
    nodes: built.nodes,
    edges: built.edges,
    entryNodeId: built.entryNodeId,
  };
}

/** + Envio com reenvio = Envio → Acompanhar entrega → (Falha) → Envio → … */
export const expandEnvio = (
  catalog: WorkflowCatalog,
  originPosition?: { x: number; y: number },
) => expandSpecSnippet(catalog, 'envio', originPosition);

/**
 * + Envio com reenvio e inatividade = Envio → Acompanhar entrega →
 * (Falha) → Envio, ou (Parado) → Cancelar → Envio → … Gravado como `envio`.
 */
export const expandEnvioInatividade = (
  catalog: WorkflowCatalog,
  originPosition?: { x: number; y: number },
) => expandSpecSnippet(catalog, 'envio-inatividade', originPosition);

/** + Inatividade = Acompanhar entrega → (Parado) → Cancelar → Envio → … */
export const expandInatividade = (
  catalog: WorkflowCatalog,
  originPosition?: { x: number; y: number },
) => expandSpecSnippet(catalog, 'inatividade', originPosition);

/* ----- Remontagem ----- */

/**
 * Remonta o grupo com a receita nova. Passo que continua no mesmo papel
 * mantém id, posição e config; o resto sai. Ficam a ligação que entra no
 * grupo e as que saem dele por uma porta que ainda existe e que a receita
 * não usa. Sem espaço no fluxo para os passos novos, `null`.
 */
export function rebuildSnippet(
  catalog: WorkflowCatalog,
  graph: WorkflowGraph,
  snippetId: string,
  spec: SnippetSpec,
): WorkflowGraph | null {
  const snippet = graph.snippets.find((s) => s.id === snippetId);
  if (!snippet?.spec) return null;
  const roles = snippet.roles ?? {};
  const oldIds = new Set(snippet.nodeIds);
  const oldNodes = graph.nodes.filter((n) => oldIds.has(n.id));

  const existing = new Map<Role, WorkflowNode>();
  for (const n of oldNodes) {
    const role = roles[n.id];
    if (role) existing.set(role, n);
  }
  const entryRole = spec.startsWithDispatch ? 'disp:0' : 'track:0';
  const anchor = existing.get(entryRole)?.position ??
    oldNodes[0]?.position ?? { x: 0, y: 0 };

  const built = buildSnippet(catalog, spec, snippetId, anchor, existing);
  const grow = built.nodes.length - oldNodes.length;
  if (grow > 0 && nodesRoomLeft(graph.nodes.length, catalog) < grow) {
    return null;
  }

  const builtById = new Map(built.nodes.map((n) => [n.id, n]));
  const usedPorts = new Set(
    built.edges.map((e) => `${e.fromNodeId}:${e.fromPortId}`),
  );
  const kept = graph.edges.filter((e) => {
    const fromIn = oldIds.has(e.fromNodeId);
    const toIn = oldIds.has(e.toNodeId);
    if (fromIn && toIn) return false;
    if (toIn) return builtById.has(e.toNodeId);
    if (!fromIn) return true;
    const node = builtById.get(e.fromNodeId);
    if (!node || usedPorts.has(`${e.fromNodeId}:${e.fromPortId}`)) {
      return false;
    }
    return outputPortIds(catalog, node).includes(e.fromPortId);
  });

  return {
    ...graph,
    nodes: [...graph.nodes.filter((n) => !oldIds.has(n.id)), ...built.nodes],
    edges: [...kept, ...built.edges],
    snippets: graph.snippets.map((s) =>
      s.id === snippetId
        ? { ...s, nodeIds: built.nodes.map((n) => n.id), spec, roles: built.roles }
        : s,
    ),
  };
}

/** Grupo com receita a que o passo pertence, e o papel do passo nele. */
export function specSnippetOf(
  graph: WorkflowGraph,
  nodeId: string,
): { snippet: WorkflowSnippet; spec: SnippetSpec; role: string } | null {
  for (const snippet of graph.snippets) {
    const role = snippet.roles?.[nodeId];
    if (snippet.spec && role && snippet.nodeIds.includes(nodeId)) {
      return { snippet, spec: snippet.spec, role };
    }
  }
  return null;
}

/**
 * Regra mexida num Acompanhar entrega do grupo vale para todos: a receita
 * passa a usar as regras e o prazo dele, e o grupo é remontado (ligar o
 * Parado cria os Cancelar entrega; tirar a Falha desfaz as ligações dela).
 */
export function syncSnippetFromTrack(
  catalog: WorkflowCatalog,
  graph: WorkflowGraph,
  nodeId: string,
): WorkflowGraph {
  const found = specSnippetOf(graph, nodeId);
  if (!found || roleName(found.role) !== 'track') return graph;
  const node = graph.nodes.find((n) => n.id === nodeId);
  if (!node) return graph;
  const cfg = node.config as TrackDeliveryConfig;
  const spec: SnippetSpec = {
    ...found.spec,
    track: {
      rules: cfg.rules ?? [],
      maxMinutes: cfg.maxMinutes ?? TRACK_MAX_MINUTES,
    },
  };
  return rebuildSnippet(catalog, graph, found.snippet.id, spec) ?? graph;
}

/** Passo do grupo que ocupa o papel (`disp:1`, `track:0`, `cancel:0`, `tag`). */
export function snippetNodeByRole(
  graph: WorkflowGraph,
  snippet: WorkflowSnippet,
  role: string,
): WorkflowNode | undefined {
  const id = Object.keys(snippet.roles ?? {}).find(
    (nodeId) => snippet.roles?.[nodeId] === role,
  );
  return id ? graph.nodes.find((n) => n.id === id) : undefined;
}

/* ----- Grupo antigo ----- */

/** O passo de envio do grupo, conforme o perfil. */
function makeSendNode(
  catalog: WorkflowCatalog,
  position: { x: number; y: number },
): WorkflowNode {
  return dispatchKindOf(catalog) === 'offer'
    ? makeNode(catalog, 'offer', position)
    : makeNode(catalog, 'disp', position, {
        title: '',
        action: 'CHEAPEST_METHOD',
        excludeTried: true,
      });
}

/**
 * + Reenvio (antigo) = Aguardar evento (as falhas do envio) → Envio. Não
 * entra mais pela paleta; fica para grafos salvos com ele.
 */
export function expandReenvio(
  catalog: WorkflowCatalog,
  originPosition = { x: 0, y: 0 },
): SnippetExpansion {
  const eventNode = makeNode(
    catalog,
    'event',
    { x: originPosition.x, y: originPosition.y },
    { onStatuses: eventStatusCodes(catalog) },
  );
  const dispNode = makeSendNode(catalog, {
    x: originPosition.x + STEP_X,
    y: originPosition.y,
  });

  const snippetId = `snippet_${nanoid(6)}`;
  eventNode.snippetId = snippetId;
  dispNode.snippetId = snippetId;

  const portId = primaryOutputPortId(catalog, eventNode);
  const edge: WorkflowEdge = {
    id: newId('edge'),
    fromNodeId: eventNode.id,
    fromPortId: portId,
    toNodeId: dispNode.id,
    style: edgeStyleForPort(catalog, eventNode, portId),
  };

  return {
    snippet: {
      id: snippetId,
      kind: 'reenvio',
      nodeIds: [eventNode.id, dispNode.id],
      label: SNIPPET_LABEL.reenvio,
    },
    nodes: [eventNode, dispNode],
    edges: [edge],
    entryNodeId: eventNode.id,
  };
}
